import { FieldWrapper } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'lib-shared-hook-form';
import { ComponentPropsWithRef, useCallback, useRef, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

// eslint-disable-next-line no-restricted-imports
import { ValidationState } from 'components/shared/util/richTextField/components/components/stateBar/stateBar.types';
// eslint-disable-next-line no-restricted-imports
import RichTextField, {
  RichTextFieldRef,
} from 'components/shared/util/richTextField/components/richTextField';

import styles from './hookFormRichTextField.module.scss';

type TextareaProps = {
  showCharacterLimit?: boolean;
} & ComponentPropsWithRef<typeof RichTextField>;

/**
 * Represents the React Hook Form variant of the {@link RichTextField} component.
 * todo: render templates with context and handle attachments
 */
export const HookFormRichTextField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<TFieldValues, TName, TextareaProps> & {
    contentType?: 'html' | 'plain';
  },
): JSX.Element => {
  const editorRef = useRef<RichTextFieldRef | null>(null);
  const { controller, fieldWrapperProps } = useHookFormFieldWrapper(props);

  const { label, contentType } = props;
  const noLabel = !label;

  const [validationState, setValidationState] = useState<ValidationState>({
    touched: false,
    valid: true,
  });

  const handleChange = useCallback(() => {
    const text =
      (contentType === 'html'
        ? editorRef.current?.getText()
        : editorRef.current?.getPlainText()) ?? '';
    controller.field.onChange(text);

    if (!validationState.touched && text.length > 0) {
      setValidationState({ touched: true, valid: true });
    }
  }, [validationState, contentType, controller.field]);

  return (
    <FieldWrapper {...fieldWrapperProps} noLabel={noLabel}>
      <RichTextField
        ref={editorRef}
        id={props.name}
        className={classnames(props.componentProps?.className, styles.textArea)}
        value=""
        onChange={handleChange}
        validation={
          props.componentProps?.showCharacterLimit ? validationState : undefined
        }
        setValidationState={setValidationState}
        stateBarClassName={styles.stateBar}
        {...props.componentProps}
      />
    </FieldWrapper>
  );
};
